import React, { useState } from "react";
import { useNotification } from "../../hooks/useNotification";
import { UserAutocomplete, User as AutocompleteUser } from "../UserAutocomplete";
import { createPayment } from "../../api/users";

interface AddPaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPaymentCreated: () => void;
}

interface PaymentFormData {
    user_id: string;
    company_id: string;
    order: string;
    orders_unity_id: string;
    payze_id: string;
    sum: string;
    total: string;
    type: string;
    type2: string;
    desc: string;
    additional_desc: string;
    time: string;
    sort: string;
    currency: string;
    rabotnik: string;
    anual: string;
}

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
    isOpen,
    onClose,
    onPaymentCreated,
}) => {
    const { showNotification } = useNotification();
    const [isSaving, setIsSaving] = useState(false);
    const [selectedUser, setSelectedUser] = useState<AutocompleteUser | null>(null);

    const [formData, setFormData] = useState<PaymentFormData>({
        user_id: "",
        company_id: "0",
        order: "0",
        orders_unity_id: "",
        payze_id: "0",
        sum: "",
        total: "",
        type: "0",
        type2: "1",
        desc: "",
        additional_desc: "",
        time: new Date().toISOString().slice(0, 16),
        sort: "0",
        currency: "0",
        rabotnik: "0",
        anual: "",
    });

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleUserSelect = (user: AutocompleteUser) => {
        setSelectedUser(user);
        setFormData((prev) => ({
            ...prev,
            user_id: user.id.toString(),
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            const paymentData = {
                user_id: parseInt(formData.user_id),
                company_id: parseInt(formData.company_id) || 0,
                order: parseInt(formData.order) || 0,
                orders_unity_id: formData.orders_unity_id || "",
                payze_id: parseInt(formData.payze_id) || 0,
                sum: parseFloat(formData.sum) || 0,
                total: parseFloat(formData.total) || 0,
                type: parseInt(formData.type) || 0,
                type2: parseInt(formData.type2) || 1,
                desc: formData.desc || "",
                additional_desc: formData.additional_desc || "",
                time: formData.time ? new Date(formData.time).toISOString() : new Date().toISOString(),
                sort: parseInt(formData.sort) || 0,
                currency: parseInt(formData.currency) || 0,
                rabotnik: parseInt(formData.rabotnik) || 0,
                anual: formData.anual || "",
            };

            await createPayment(paymentData);
            showNotification("Запись успешно создана", "success");
            onPaymentCreated();
            handleClose();
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Ошибка при создании записи";
            showNotification(errorMessage, "error");
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setFormData({
            user_id: "",
            company_id: "0",
            order: "0",
            orders_unity_id: "",
            payze_id: "0",
            sum: "",
            total: "",
            type: "0",
            type2: "1",
            desc: "",
            additional_desc: "",
            time: new Date().toISOString().slice(0, 16),
            sort: "0",
            currency: "0",
            rabotnik: "0",
            anual: "",
        });
        setSelectedUser(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                    onClick={handleClose}
                ></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>

                <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    <form onSubmit={handleSubmit}>
                        <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                                    Добавить запись в статистику
                                </h3>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                                >
                                    <svg
                                        className="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {/* User ID */}
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Пользователь *
                                    </label>
                                    <div className="mt-1">
                                        <UserAutocomplete
                                            value={formData.user_id}
                                            onChange={(value) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    user_id: value,
                                                }))
                                            }
                                            onUserSelect={handleUserSelect}
                                            placeholder="ID, email, phone, name..."
                                        />
                                    </div>
                                </div>

                                {/* Sum */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Сумма (sum) *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="sum"
                                        value={formData.sum}
                                        onChange={handleInputChange}
                                        required
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Total */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Итого (total) *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="total"
                                        value={formData.total}
                                        onChange={handleInputChange}
                                        required
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Тип (type) *
                                    </label>
                                    <input
                                        type="number"
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                        required
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Type2 */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Тип транзакции (type2) *
                                    </label>
                                    <select
                                        name="type2"
                                        value={formData.type2}
                                        onChange={handleInputChange}
                                        required
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="0">0 - Расход</option>
                                        <option value="1">1 - Приход</option>
                                        <option value="2">2 - Нейтрально</option>
                                    </select>
                                </div>

                                {/* Desc */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Описание (desc) *
                                    </label>
                                    <input
                                        type="text"
                                        name="desc"
                                        value={formData.desc}
                                        onChange={handleInputChange}
                                        maxLength={50}
                                        required
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Additional Desc */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Дополнительное описание (additional_desc)
                                    </label>
                                    <textarea
                                        name="additional_desc"
                                        value={formData.additional_desc}
                                        onChange={handleInputChange}
                                        rows={2}
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Time */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Время (time) *
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="time"
                                        value={formData.time}
                                        onChange={handleInputChange}
                                        required
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Currency */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Валюта (currency) *
                                    </label>
                                    <select
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleInputChange}
                                        required
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="0">0 - Доллар</option>
                                        <option value="1">1 - Крипта</option>
                                        <option value="100">100 - Нет валюты</option>
                                    </select>
                                </div>

                                {/* Sort */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Кому принадлежит (sort) *
                                    </label>
                                    <select
                                        name="sort"
                                        value={formData.sort}
                                        onChange={handleInputChange}
                                        required
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="0">0 - Клиент/Менеджер</option>
                                        <option value="1">1 - Дилеры</option>
                                        <option value="2">2 - Компании</option>
                                        <option value="3">3 - Курьер</option>
                                    </select>
                                </div>

                                {/* Company ID */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        ID компании (company_id)
                                    </label>
                                    <input
                                        type="number"
                                        name="company_id"
                                        value={formData.company_id}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Order */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        ID заказа (order)
                                    </label>
                                    <input
                                        type="number"
                                        name="order"
                                        value={formData.order}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Orders Unity ID */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Orders Unity ID
                                    </label>
                                    <input
                                        type="text"
                                        name="orders_unity_id"
                                        value={formData.orders_unity_id}
                                        onChange={handleInputChange}
                                        maxLength={20}
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Payze ID */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Payze ID (payze_id)
                                    </label>
                                    <input
                                        type="number"
                                        name="payze_id"
                                        value={formData.payze_id}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Rabotnik */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Работник (rabotnik)
                                    </label>
                                    <input
                                        type="number"
                                        name="rabotnik"
                                        value={formData.rabotnik}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                {/* Anual */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Годовая программа (anual)
                                    </label>
                                    <select
                                        name="anual"
                                        value={formData.anual}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="">Нет</option>
                                        <option value="1">1 - Структура</option>
                                        <option value="2">2 - Благотворительность</option>
                                        <option value="3">3 - Туризм</option>
                                        <option value="4">4 - Звездная</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSaving ? "Сохранение..." : "Сохранить"}
                            </button>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            >
                                Отмена
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddPaymentModal;
